<template>
  <div>
    <AppTableHeader>
      <template #left>
        {{
          $t("events:EventPage.results.importLog.heading", "Results import log")
        }}
      </template>
    </AppTableHeader>
    <table class="entsys--table">
      <thead v-if="$vuetify.display.mdAndUp">
        <tr>
          <th class="text-start" style="max-width: 20%">
            {{
              $t(
                "events:EventPage.results.importLog.importedCompetitions.label",
                "Imported competitions"
              )
            }}
          </th>
          <th class="text-start">
            {{
              $t(
                "events:EventPage.results.importLog.resultsImportedAt.label",
                "Results imported at"
              )
            }}
          </th>
          <th class="text-start">
            {{
              $t(
                "events:EventPage.results.importLog.importCompletedAt.label",
                "Import completed at"
              )
            }}
          </th>
          <th>
            {{
              $t(
                "events:EventPage.results.importLog.numberOfResults.label",
                "Results"
              )
            }}
          </th>
          <th>
            {{
              $t(
                "events:EventPage.results.importLog.numberOfErrors.label",
                "Errors"
              )
            }}
          </th>
          <th>
            {{
              $t(
                "events:EventPage.results.importLog.statusOfImport.label",
                "Status"
              )
            }}
          </th>
          <th>
            {{
              $t("events:EventPage.results.importLog.details.label", "Details")
            }}
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="resultImport in resultsImports" :key="resultImport.id">
          <tr>
            <template v-if="$vuetify.display.mdAndUp">
              <td>{{ competitionCodesSorted(resultImport) }}</td>
              <td>{{ $dayjs.date(resultImport.createdAt, "fullTime") }}</td>
              <td>
                {{ $dayjs.date(resultImport.processingEndedAt, "fullTime") }}
              </td>
              <td>
                <AppShowChipBoxes
                  align="center"
                  :items="[
                    {
                      name: 'payloadResultsCount',
                      value: resultImport.payloadResultsCount
                    }
                  ]"
                />
              </td>
              <td>
                <AppShowChipBoxes
                  v-if="resultImport.feedbackMessagesCount > 0"
                  color="text-red"
                  align="center"
                  :items="[
                    {
                      name: 'feedbackMessagesCount',
                      value: resultImport.feedbackMessagesCount
                    }
                  ]"
                >
                  <template #feedbackMessagesCount="{ value }">
                    <span :class="{ 'text-red': true }">
                      {{ value }}
                    </span>
                  </template>
                </AppShowChipBoxes>
              </td>
              <td class="text-center">
                <AppChip
                  v-if="resultImport.status"
                  v-bind="badgeStatus(resultImport)"
                >
                  {{ badgeText(resultImport) }}
                </AppChip>
              </td>
            </template>
            <td v-else colspan="99">
              <div class="d-flex">
                <div style="flex-grow: 1">
                  <div class="mb-2">
                    <b>
                      {{
                        $t(
                          "events:EventPage.results.importLog.importedCompetitions.label",
                          "Imported competitions"
                        )
                      }}</b
                    ><br />{{ competitionCodesSorted(resultImport) }}
                  </div>
                  <div class="mb-2">
                    <b>
                      {{
                        $t(
                          "events:EventPage.results.importLog.resultsImportedAt.label",
                          "Results imported at"
                        )
                      }}</b
                    ><br />{{ $dayjs.date(resultImport.createdAt, "fullTime") }}
                  </div>
                  <div class="mb-2">
                    <b>
                      {{
                        $t(
                          "events:EventPage.results.importLog.importCompletedAt.label",
                          "Import completed at"
                        )
                      }}</b
                    ><br />{{
                      $dayjs.date(resultImport.processingEndedAt, "fullTime")
                    }}
                  </div>
                </div>
                <div class="text-center" style="flex-grow: 1">
                  <div class="mb-2">
                    <b>
                      {{
                        $t(
                          "events:EventPage.results.importLog.numberOfResults.label",
                          "Results"
                        )
                      }}</b
                    >

                    <AppShowChipBoxes
                      align="center"
                      :items="[
                        {
                          name: 'payloadResultsCount',
                          value: resultImport.payloadResultsCount
                        }
                      ]"
                    />
                  </div>

                  <div
                    v-if="resultImport.feedbackMessagesCount > 0"
                    class="mb-2"
                  >
                    <b>{{
                      $t(
                        "events:EventPage.results.importLog.numberOfErrors.label",
                        "Errors"
                      )
                    }}</b>
                    <AppShowChipBoxes
                      color="text-red"
                      align="center"
                      :items="[
                        {
                          name: 'feedbackMessagesCount',
                          value: resultImport.feedbackMessagesCount
                        }
                      ]"
                    >
                      <template #feedbackMessagesCount="{ value }">
                        <span :class="{ 'text-red': true }">
                          {{ value }}
                        </span>
                      </template>
                    </AppShowChipBoxes>
                  </div>

                  <div class="mb-2">
                    <AppChip
                      v-if="resultImport.status"
                      v-bind="badgeStatus(resultImport)"
                    >
                      {{ badgeText(resultImport) }}
                    </AppChip>
                  </div>
                </div>
              </div>
            </td>

            <td class="text-center">
              <v-btn
                v-if="resultImport.payloadFile"
                :href="prependApiDomain(resultImport.payloadFile.path)"
                variant="outlined"
                size="small"
                target="_blank"
              >
                {{ $t("common:download.button") }}
              </v-btn>
              <v-btn
                v-else-if="resultImport.payload"
                variant="outlined"
                size="small"
                @click="() => downloadBlob(resultImport.payload)"
              >
                {{ $t("common:download.button") }}
              </v-btn>

              <v-btn-expand
                v-if="resultImport.feedbackMessagesCount > 0"
                data-test="expand"
                :model-value="showExpanded[resultImport.id]"
                @click="
                  () =>
                    (showExpanded[resultImport.id] =
                      !showExpanded[resultImport.id])
                "
              >
              </v-btn-expand>
            </td>
          </tr>
          <tr
            v-if="resultImport.feedbackMessagesCount > 0"
            v-show="showExpanded[resultImport.id]"
            :key="'expanded-' + resultImport.id"
          >
            <td colspan="99">
              <ul>
                <li
                  v-for="(feedback, index) in resultImport.feedbackMessages"
                  :key="index"
                >
                  {{ displayText(feedback) }}
                </li>
              </ul>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
<script>
import gql from "fraql";
export default {
  name: "ResultImportLogs",
  fragments: {
    eventFields: gql`
      fragment _ on Event {
        referenceCodeForDisplay
      }
    `,
    resultImportFields: gql`
      fragment _ on ResultsImport {
        payloadFile {
          id
          path
        }
        payload
      }
    `
  }
};
</script>
<script setup>
// name: "ResultImportLogs"
import AppTableHeader from "@/components/elements/table/AppTableHeader.vue";
import AppShowChipBoxes from "@AppShows/AppShowChipBoxes.vue";
import { clone } from "lodash-es";
import { reactive, watch } from "vue";
import { useDisplayText } from "@/composables/display_text";
import AppChip from "@elements/AppChip.vue";
import { useTranslation } from "i18next-vue";
import { useCompetitionHelpers } from "@composables/competition_helpers";
import { exportEventFilenameUtility } from "@/utils/exportFilenameUtilities";
import { prependApiDomain } from "@/utils/prependApiDomain";

const props = defineProps({
  event: {
    type: Object,
    required: true
  },
  resultsImports: {
    type: Array,
    required: true
  }
});

const { displayText } = useDisplayText();
const { t } = useTranslation();
const { competitionOrderFn } = useCompetitionHelpers();
const showExpanded = reactive({});

watch(
  () => props.resultsImports,
  (imports = []) => {
    imports.forEach(i => {
      if (showExpanded[i.id] == undefined) {
        showExpanded[i.id] = false;
      }
    });
  },
  { immediate: true }
);

function competitionCodesSorted(resultImport) {
  const compare = competitionOrderFn.value;
  return clone(resultImport?.payloadCompetitionCodes ?? [])
    .sort(compare)
    .join(", ");
}

function badgeStatus(resultImport) {
  switch (resultImport.status) {
    case "IMPORTED":
    case "PROCESSING":
      return {
        color: "muted",
        textColor: "white"
      };
    case "PROCESSED":
      return {
        color: "#1BA348",
        textColor: "white"
      };
    case "FAILED":
      return {
        color: "#D0021B",
        textColor: "white"
      };
  }
}

function badgeText(resultImport) {
  switch (resultImport.status) {
    case "IMPORTED":
      return t(
        "events:EventPage.results.importLog.statusOfImport.IMPORTED.badge",
        "waiting in queue"
      );
    case "PROCESSING":
      return t(
        "events:EventPage.results.importLog.statusOfImport.PROCESSING.badge",
        "processing"
      );
    case "PROCESSED":
      return t(
        "events:EventPage.results.importLog.statusOfImport.PROCESSED.badge",
        "completed"
      );
    case "FAILED":
      return t(
        "events:EventPage.results.importLog.statusOfImport.FAILED.badge",
        "failed"
      );
  }
}

function downloadBlob(payload) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(
    new Blob([JSON.stringify(payload)], {
      type: "application/json"
    })
  );
  link.download = `${exportEventFilenameUtility(
    "results-import",
    props.event.referenceCodeForDisplay
  )}.json`;
  link.click();
}
</script>
